import { useEffect, useRef } from "react";
import { logger } from "@/lib/logger";

/**
 * Global feed WebSocket. Fires onNewOrder(order) when a matching order arrives.
 * Uses a ref for the callback to avoid reconnecting when the caller passes
 * an inline function.
 */
export default function useFeedNotifications(enabled, onNewOrder) {
  const wsRef = useRef(null);
  const cbRef = useRef(onNewOrder);

  // Keep the latest callback in a ref so the effect below doesn't need it
  // in its dependency array (which would tear down and rebuild the socket
  // on every parent re-render).
  useEffect(() => {
    cbRef.current = onNewOrder;
  }, [onNewOrder]);

  useEffect(() => {
    if (!enabled) return;
    const token = localStorage.getItem("rivendas_token") || "";
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/feed?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      logger.warn("feed ws connect failed", err?.message);
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === "new_order" && payload.order) {
          cbRef.current?.(payload.order);
        }
      } catch (err) {
        logger.warn("feed ws parse", err?.message);
      }
    };
    ws.onerror = (e) => logger.warn("feed ws error", e);
    wsRef.current = ws;
    return () => {
      try { ws.close(); } catch (err) { logger.warn("feed ws close", err?.message); }
      wsRef.current = null;
    };
  }, [enabled]);
}
